import { useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { CircleUserRound, Search } from 'lucide-react';
import { authService } from '../services';
import { Z_INDEX, getZIndexClass } from '../utils/zIndexUtils';

// Paths where the bottom navigation should be displayed
const SHOW_NAVIGATION_PATHS = [
  '/flowers',
  '/fruits',
  '/cat-grass',
  '/home',
  '/subscriptions',
  '/me',
  '/delivery-activity',
  '/billing-history',
  '/refund-history',
  '/upcoming-services',
  '/gift-subscription',
  '/gift'
];

export default function BottomNavigation() {
  const location = useLocation();
  const currentPath = location.pathname;
  const { t } = useTranslation();
  const isAuthenticated = authService.isAuthenticated();
  
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [scrollTimeout, setScrollTimeout] = useState<number | null>(null);
  const [scrollDebounceTimeout, setScrollDebounceTimeout] = useState<number | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Check if user is at the bottom of the page
      const isAtBottom = window.innerHeight + window.scrollY >= document.body.offsetHeight - 10;
      
      // Always show navigation when at bottom of page
      if (isAtBottom) {
        setIsVisible(true);
        setLastScrollY(currentScrollY);
        return;
      }
      
      // Clear existing timeouts
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      if (scrollDebounceTimeout) {
        clearTimeout(scrollDebounceTimeout);
      }
      
      // Debounce scroll handling to make it less responsive
      const debounceTimeout = setTimeout(() => {
        const scrollDifference = Math.abs(currentScrollY - lastScrollY);
        
        // Only react to significant scroll movements (at least 20px)
        if (scrollDifference < 20) return;
        
        // Show navigation if scrolling up or at top
        if (currentScrollY < lastScrollY || currentScrollY < 20) {
          setIsVisible(true);
        } 
        // Hide navigation if scrolling down (with higher threshold)
        else if (currentScrollY > lastScrollY && currentScrollY > 100) {
          setIsVisible(false);
        }
        
        setLastScrollY(currentScrollY);
      }, 100); // 100ms debounce delay
      
      setScrollDebounceTimeout(debounceTimeout);
      
      // Show navigation after stopping scroll for longer duration
      const timeout = setTimeout(() => {
        setIsVisible(true);
      }, 500); // Increased from 150ms to 500ms
      
      setScrollTimeout(timeout);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      if (scrollDebounceTimeout) {
        clearTimeout(scrollDebounceTimeout);
      }
    };
  }, [lastScrollY, scrollTimeout, scrollDebounceTimeout]);
  
  // Check if the current path is a subscription detail page (with UUID)
  const isSubscriptionDetailPage = /\/subscriptions\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/.test(currentPath);
  
  // Check if navigation should be shown on current page
  const shouldShowNavigation = SHOW_NAVIGATION_PATHS.some(path => 
    currentPath.includes(path) || currentPath === path
  ) && !isSubscriptionDetailPage;
  
  if (!shouldShowNavigation) {
    return null;
  }

  // Different navigation items based on authentication status
  const unauthenticatedNavItems = [
    {
      to: "/flowers",
      label: t('navigation.explore'),
      isActive: currentPath === '/flowers' || currentPath === '/home' || currentPath === '/fruits',
      icon: <Search className="h-6 w-6" />
    },
    {
      to: "/gift-subscription",
      label: t('navigation.gift'),
      isActive: currentPath.includes('/gift-subscription') || currentPath.includes('/gift'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <rect x="3" y="8" width="18" height="4" rx="1" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}></rect>
          <path d="M12 8v13" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}></path>
          <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}></path>
          <path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}></path>
        </svg>
      )
    },
    {
      to: "/login",
      label: t('navigation.login'),
      isActive: currentPath === '/login' || currentPath === '/register',
      icon: <CircleUserRound className="h-6 w-6" />
    }
  ];

  const authenticatedNavItems = [
    {
      to: "/flowers",
      label: t('navigation.explore'),
      isActive: currentPath === '/flowers' || currentPath === '/home' || currentPath === '/fruits',
      icon: <Search className="h-6 w-6" />
    },
    {
      to: "/subscriptions",
      label: t('navigation.subscriptions'),
      isActive: currentPath.includes('/subscription') || currentPath.includes('/upcoming-services'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      )
    },
    {
      to: "/gift-subscription",
      label: t('navigation.gift'),
      isActive: currentPath.includes('/gift-subscription') || currentPath.includes('/gift'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <rect x="3" y="8" width="18" height="4" rx="1" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}></rect>
          <path d="M12 8v13" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}></path>
          <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}></path>
          <path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}></path>
        </svg>
      )
    },
    {
      to: "/me",
      label: t('navigation.myAccount'),
      isActive: currentPath === '/me' || currentPath.includes('/delivery-activity') || currentPath.includes('/billing-history') || currentPath.includes('/refund-history'),
      icon: <CircleUserRound className="h-6 w-6" />
    }
  ];

  const navItems = isAuthenticated ? authenticatedNavItems : unauthenticatedNavItems;

  return (
    <div className={`fixed bottom-0 left-0 right-0 h-16 bg-white flex ${getZIndexClass(Z_INDEX.BOTTOM_NAVIGATION)} max-w-[430px] mx-auto border-t border-gray-100 transition-transform duration-300 ease-in-out ${
      isVisible ? 'translate-y-0' : 'translate-y-full'
    }`}>
      {navItems.map((item, index) => (
        <NavItem 
          key={index}
          to={item.to}
          label={item.label}
          isActive={item.isActive}
          icon={item.icon}
          width="flex-1"
        />
      ))}
    </div>
  );
}

interface NavItemProps {
  to: string;
  label: string;
  isActive: boolean;
  icon: React.ReactNode;
  width: string;
}

function NavItem({ to, label, isActive, icon, width }: NavItemProps) {
  return (
    <Link 
      to={to} 
      className={`${width} flex flex-col items-center justify-center h-full py-2 ${
        isActive ? 'text-amber-400' : 'text-gray-400'
      }`}
    >
      <div className="mb-1">{icon}</div>
      <span className="text-xs whitespace-nowrap">{label}</span>
    </Link>
  );
} 